import type { MedplumClient } from "@medplum/core";
import type {
  CodeableConcept,
  Coding,
  Identifier,
  Observation,
  ObservationComponent,
  Period,
  Reference,
  SampledData,
} from "@medplum/fhirtypes";
import type { CreateResourceOptions } from "../fhir/FhirResource";
import { FhirResource } from "../fhir/FhirResource";
import { ObservationStatistics } from "./loinc";
import { normalizeTimezoneOffsetSeconds } from "./timezone-offset";
import {
  WEARABLE_IDENTIFIER_SYSTEM_JUNCTION_ID,
  WEARABLE_IDENTIFIER_SYSTEM_PROVIDER,
} from "./types";

const PROVIDER_SLUG_EXTENSION = "vibrant.wearable.provider-slug";
const LOCAL_DAY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * How a stored wearable value was arrived at, when it is NOT the number the provider reported.
 *
 * Absent means the value came straight from the provider (or predates this extension) - stamping
 * only computed values keeps "provider-reported" as the default reading of an unstamped row.
 *
 * - `in-bed-minus-awake`: sleep duration from the in-bed span less reported awake time, used when
 *   a provider supplies no asleep stages or total (Whoop / Oura via Apple Health).
 * - `in-bed-times-efficiency`: sleep duration from the in-bed span scaled by reported efficiency.
 * - `asleep-over-in-bed`: sleep efficiency recomputed as asleep / in-bed.
 */
export type WearableValueDerivation =
  | "in-bed-minus-awake"
  | "in-bed-times-efficiency"
  | "asleep-over-in-bed";

/**
 * Data structure for creating a VitalObservation (single point-in-time).
 */
export interface VitalObservationData {
  junctionId: string;
  patientId: string;
  providerSlug?: string;
  deviceId?: string;
  effectiveDate: string;
  value: number;
  extraIdentifiers?: Identifier[];
  derivedFrom?: Reference<Observation>[];
}

/**
 * Source local-day metadata for daily wearable observations.
 */
export interface WearableDailyLocalDayData {
  timezoneOffsetSeconds?: number | null;
}

/**
 * Data structure for creating a daily aggregated VitalObservation.
 */
export interface DailyVitalObservationData extends WearableDailyLocalDayData {
  junctionId: string;
  patientId: string;
  deviceId?: string;
  providerSlug: string;
  date: string;
  value?: number;
  statistics?: {
    average?: number;
    min?: number;
    max?: number;
    count?: number;
  };
  components?: Array<{
    code: CodeableConcept;
    value?: number;
    valueString?: string;
    /** For components that describe a span rather than a reading (e.g. one hypnogram stage run). */
    valuePeriod?: Period;
    unit?: string;
    unitCode?: string;
  }>;
  sampledData?: {
    origin: number;
    periodMs: number;
    data: string;
  };
  extraIdentifiers?: Identifier[];
  /** Set only when `value` was computed by us rather than reported by the provider. */
  valueDerivation?: WearableValueDerivation;
}

/**
 * Search query parameters for VitalObservation.
 */
export interface VitalObservationSearchQuery {
  junctionId?: string;
  patientId?: string;
  loincCode?: string;
}

interface VitalObservationQuantityParams {
  unit: string;
  unitCode: string;
  category: CodeableConcept;
}

export interface CreateVitalObservationParams extends VitalObservationQuantityParams {
  data: VitalObservationData;
  loincCode: string;
  loincDisplay: string;
  options?: CreateResourceOptions;
}

export interface CreateCustomVitalObservationParams extends VitalObservationQuantityParams {
  data: VitalObservationData;
  codeSystem: string;
  code: string;
  display: string;
  options?: CreateResourceOptions;
}

export interface UpsertDailyVitalObservationParams extends VitalObservationQuantityParams {
  data: DailyVitalObservationData;
  loincCode: string;
  loincDisplay: string;
}

export interface UpsertCustomDailyVitalObservationParams extends VitalObservationQuantityParams {
  data: DailyVitalObservationData;
  codeSystem: string;
  code: string;
  display: string;
}

export interface GenerateDailyJunctionIdParams {
  metric: string;
  patientId: string;
  providerSlug: string;
  date: string;
}

export interface VitalObservationSearchOptions {
  limit?: number;
  offset?: number;
  sort?: string;
  dateGe?: string;
  dateLe?: string;
}

export interface SearchObservationsByPatientParams {
  patientId: string;
  loincCode: string;
  options?: VitalObservationSearchOptions;
}

export interface SearchObservationsByCustomCodeParams {
  patientId: string;
  codeSystem: string;
  code: string;
  options?: VitalObservationSearchOptions;
}

export interface BackfillMarkerParams {
  memberId: string;
  providerSlug: string;
  startDate: string;
  endDate: string;
}

export interface CreateBackfillCompletionMarkerParams extends BackfillMarkerParams {
  results?: {
    activity?: { fetched: number; created: number };
    sleep?: { fetched: number; created: number };
    body?: { fetched: number; created: number };
    workouts?: { fetched: number; created: number };
  };
}

/**
 * Shared canonical writable vital-observation model.
 * Supports both the lightweight read accessors and the richer web write/query helpers.
 */
export class VitalObservation extends FhirResource<Observation> {
  static readonly ResourceType = "Observation";

  static readonly Identifier = {
    JunctionId: WEARABLE_IDENTIFIER_SYSTEM_JUNCTION_ID,
    ProviderSlug: WEARABLE_IDENTIFIER_SYSTEM_PROVIDER,
    VitalsOrigin: "vibrant.vitals.origin",
    VitalsOriginNoteId: "vibrant.vitals.origin-note-id",
    VitalsOriginNodeId: "vibrant.vitals.origin-node-id",
    WearableOrigin: "vibrant.wearable.origin",
    WearableCQQueryId: "vibrant.wearable.cq-query-id",
    WearableCQQuerySlug: "vibrant.wearable.cq-query-slug",
    /**
     * Junction `source.app_id` - the app that wrote the samples (`com.whoop.iphone`,
     * `com.ouraring.oura`, ...). For Apple Health / Health Connect the provider slug only says
     * "came via Apple Health"; this is the only field that says which app actually produced it.
     */
    WearableSourceApp: "vibrant.wearable.source-app",
    /** Junction `source.device_id` - the source device the samples were recorded on. */
    WearableSourceDevice: "vibrant.wearable.source-device",
  } as const;

  static readonly Extension = {
    ProviderSlug: PROVIDER_SLUG_EXTENSION,
    TimezoneOffsetSeconds: "vibrant.observation.wearable-timezone-offset-seconds",
    ValueDerivation: "vibrant.observation.wearable-value-derivation",
  } as const;

  //-----------------------------------------------------------------------------------------------
  //                                          GETTERS
  //-----------------------------------------------------------------------------------------------

  get loincCode(): string | undefined {
    return getLoincCoding(this.resource)?.code;
  }

  get primaryCode(): string | undefined {
    return getPrimaryCoding(this.resource)?.code;
  }

  get displayName(): string | undefined {
    return getPrimaryCoding(this.resource)?.display ?? this.resource.code?.text;
  }

  get junctionId(): string {
    return (
      this.resource.identifier?.find(
        (identifier) => identifier.system === VitalObservation.Identifier.JunctionId,
      )?.value || ""
    );
  }

  get patientId(): string {
    return this.resource.subject?.reference?.split("/")[1] || "";
  }

  get deviceId(): string | undefined {
    return this.resource.device?.reference?.split("/")[1];
  }

  get deviceReference(): string | undefined {
    return this.resource.device?.reference;
  }

  get effectiveDate(): string | undefined {
    return this.resource.effectiveDateTime ?? this.resource.effectivePeriod?.start;
  }

  get effectiveDateOnly(): string | undefined {
    return this.date;
  }

  get effectivePeriod(): Period | undefined {
    return this.resource.effectivePeriod;
  }

  get date(): string | undefined {
    if (this.resource.effectiveDateTime) {
      return this.resource.effectiveDateTime.split("T")[0];
    }

    if (this.resource.effectivePeriod?.start) {
      const localDate = getLocalDateFromInstant({
        instant: this.resource.effectivePeriod.start,
        timezoneOffsetSeconds: this.timezoneOffsetSeconds,
      });
      if (localDate) {
        return localDate;
      }

      return this.resource.effectivePeriod.start.split("T")[0];
    }

    return undefined;
  }

  get value(): number | undefined {
    return this.resource.valueQuantity?.value;
  }

  /** How `value` was computed, when it is not the provider's own reported number. */
  get valueDerivation(): WearableValueDerivation | undefined {
    return this.getExtension(VitalObservation.Extension.ValueDerivation)?.valueCode as
      | WearableValueDerivation
      | undefined;
  }

  get unit(): string | undefined {
    return this.resource.valueQuantity?.unit ?? this.resource.valueQuantity?.code;
  }

  get valueString(): string | undefined {
    return this.resource.valueString;
  }

  get providerSlug(): string | undefined {
    return (
      this.resource.identifier?.find(
        (identifier) => identifier.system === VitalObservation.Identifier.ProviderSlug,
      )?.value || this.getExtension(VitalObservation.Extension.ProviderSlug)?.valueString
    );
  }

  get timezoneOffsetSeconds(): number | undefined {
    return this.getExtension(VitalObservation.Extension.TimezoneOffsetSeconds)?.valueInteger;
  }

  get status(): Observation["status"] {
    return this.resource.status;
  }

  get isFinal(): boolean {
    return this.resource.status === "final";
  }

  get components(): ObservationComponent[] {
    return this.resource.component ?? [];
  }

  findComponent(code: string): ObservationComponent | undefined {
    return this.resource.component?.find((component) =>
      component.code?.coding?.some((coding) => coding.code === code),
    );
  }

  getComponentValue(code: string): number | undefined {
    return this.findComponent(code)?.valueQuantity?.value;
  }

  get statistics(): { average?: number; min?: number; max?: number; count?: number } {
    return {
      average: this.getComponentValue("average"),
      min: this.getComponentValue("min"),
      max: this.getComponentValue("max"),
      count: this.getComponentValue("count"),
    };
  }

  get sampledData(): SampledData | undefined {
    return this.resource.valueSampledData;
  }

  //-----------------------------------------------------------------------------------------------
  //                                     PURE CONVERTERS
  //-----------------------------------------------------------------------------------------------

  private static toFhirQuery(query: VitalObservationSearchQuery) {
    return {
      identifier: query.junctionId
        ? `${VitalObservation.Identifier.JunctionId}|${query.junctionId}`
        : undefined,
      subject: query.patientId ? `Patient/${query.patientId}` : undefined,
      code: query.loincCode ? `http://loinc.org|${query.loincCode}` : undefined,
    };
  }

  //-----------------------------------------------------------------------------------------------
  //                                      STATIC METHODS
  //-----------------------------------------------------------------------------------------------

  protected static async createVitalObservation<T extends VitalObservation>(
    this: new (
      observation: Observation,
    ) => T,
    medplum: MedplumClient,
    {
      data,
      loincCode,
      loincDisplay,
      unit,
      unitCode,
      category,
      options,
    }: CreateVitalObservationParams,
  ): Promise<T> {
    const observationData = {
      identifier: buildObservationIdentifiers({
        junctionId: data.junctionId,
        providerSlug: data.providerSlug,
        extraIdentifiers: data.extraIdentifiers,
      }),
      status: "final" as const,
      category: [category],
      code: {
        coding: [
          {
            system: "http://loinc.org",
            code: loincCode,
            display: loincDisplay,
          },
        ],
        text: loincDisplay,
      },
      subject: {
        reference: `Patient/${data.patientId}`,
      },
      device: data.deviceId
        ? {
            reference: `Device/${data.deviceId}`,
          }
        : undefined,
      effectiveDateTime: data.effectiveDate,
      valueQuantity: {
        value: data.value,
        unit,
        system: "http://unitsofmeasure.org",
        code: unitCode,
      },
      derivedFrom: data.derivedFrom?.length ? data.derivedFrom : undefined,
    };

    const created = await VitalObservation.createResource(medplum, observationData, options);
    // biome-ignore lint: subclasses should receive their own concrete model type.
    const Model = this;
    return new Model(created);
  }

  protected static async createCustomVitalObservation<T extends VitalObservation>(
    this: new (
      observation: Observation,
    ) => T,
    medplum: MedplumClient,
    {
      data,
      codeSystem,
      code,
      display,
      unit,
      unitCode,
      category,
      options,
    }: CreateCustomVitalObservationParams,
  ): Promise<T> {
    const observationData = {
      identifier: buildObservationIdentifiers({
        junctionId: data.junctionId,
        providerSlug: data.providerSlug,
        extraIdentifiers: data.extraIdentifiers,
      }),
      status: "final" as const,
      category: [category],
      code: {
        coding: [
          {
            system: codeSystem,
            code,
            display,
          },
        ],
        text: display,
      },
      subject: {
        reference: `Patient/${data.patientId}`,
      },
      device: data.deviceId
        ? {
            reference: `Device/${data.deviceId}`,
          }
        : undefined,
      effectiveDateTime: data.effectiveDate,
      valueQuantity: {
        value: data.value,
        unit,
        system: "http://unitsofmeasure.org",
        code: unitCode,
      },
      derivedFrom: data.derivedFrom?.length ? data.derivedFrom : undefined,
    };

    const created = await VitalObservation.createResource(medplum, observationData, options);
    // biome-ignore lint: subclasses should receive their own concrete model type.
    const Model = this;
    return new Model(created);
  }

  protected static async upsertDailyVitalObservation<T extends VitalObservation>(
    this: new (
      observation: Observation,
    ) => T,
    medplum: MedplumClient,
    { data, loincCode, loincDisplay, unit, unitCode, category }: UpsertDailyVitalObservationParams,
  ): Promise<T> {
    const query = {
      identifier: `${VitalObservation.Identifier.JunctionId}|${data.junctionId}`,
    };
    const existingResource = await VitalObservation.searchOneResource(medplum, query);

    const observationData = {
      identifier: buildObservationIdentifiers({
        junctionId: data.junctionId,
        providerSlug: data.providerSlug,
        extraIdentifiers: data.extraIdentifiers,
      }),
      status: "final" as const,
      category: [category],
      code: {
        coding: [
          {
            system: "http://loinc.org",
            code: loincCode,
            display: loincDisplay,
          },
        ],
        text: loincDisplay,
      },
      subject: {
        reference: `Patient/${data.patientId}`,
      },
      device: data.deviceId
        ? {
            reference: `Device/${data.deviceId}`,
          }
        : undefined,
      effectivePeriod: buildDayPeriod(data.date, data.timezoneOffsetSeconds),
      extension: buildDailyObservationExtensions({
        timezoneOffsetSeconds: data.timezoneOffsetSeconds,
        valueDerivation: data.valueDerivation,
        removeWhenMissing: existingResource?.id !== undefined,
      }),
      valueQuantity:
        data.value !== undefined
          ? {
              value: data.value,
              unit,
              system: "http://unitsofmeasure.org",
              code: unitCode,
            }
          : undefined,
      component: buildObservationComponents({
        statistics: data.statistics,
        components: data.components,
        unit,
        unitCode,
      }),
      valueSampledData: buildSampledData({
        sampledData: data.sampledData,
        unit,
        unitCode,
      }),
    };

    const upserted = await VitalObservation.upsertDailyObservationResource({
      medplum,
      observationData,
      existingResource,
      junctionId: data.junctionId,
    });
    // biome-ignore lint: subclasses should receive their own concrete model type.
    const Model = this;
    return new Model(upserted);
  }

  static generateDailyJunctionId({
    metric,
    patientId,
    providerSlug,
    date,
  }: GenerateDailyJunctionIdParams): string {
    return `${metric}-${patientId}-${providerSlug}-${date}`;
  }

  protected static async upsertCustomDailyVitalObservation<T extends VitalObservation>(
    this: new (
      observation: Observation,
    ) => T,
    medplum: MedplumClient,
    {
      data,
      codeSystem,
      code,
      display,
      unit,
      unitCode,
      category,
    }: UpsertCustomDailyVitalObservationParams,
  ): Promise<T> {
    const query = {
      identifier: `${VitalObservation.Identifier.JunctionId}|${data.junctionId}`,
    };
    const existingResource = await VitalObservation.searchOneResource(medplum, query);

    const observationData = {
      identifier: buildObservationIdentifiers({
        junctionId: data.junctionId,
        providerSlug: data.providerSlug,
        extraIdentifiers: data.extraIdentifiers,
      }),
      status: "final" as const,
      category: [category],
      code: {
        coding: [
          {
            system: codeSystem,
            code,
            display,
          },
        ],
        text: display,
      },
      subject: {
        reference: `Patient/${data.patientId}`,
      },
      device: data.deviceId
        ? {
            reference: `Device/${data.deviceId}`,
          }
        : undefined,
      effectivePeriod: buildDayPeriod(data.date, data.timezoneOffsetSeconds),
      extension: buildDailyObservationExtensions({
        timezoneOffsetSeconds: data.timezoneOffsetSeconds,
        valueDerivation: data.valueDerivation,
        removeWhenMissing: existingResource?.id !== undefined,
      }),
      valueQuantity:
        data.value !== undefined
          ? {
              value: data.value,
              unit,
              system: "http://unitsofmeasure.org",
              code: unitCode,
            }
          : undefined,
      component: buildObservationComponents({
        statistics: data.statistics,
        components: data.components,
        unit,
        unitCode,
      }),
    };

    const upserted = await VitalObservation.upsertDailyObservationResource({
      medplum,
      observationData,
      existingResource,
      junctionId: data.junctionId,
    });
    // biome-ignore lint: subclasses should receive their own concrete model type.
    const Model = this;
    return new Model(upserted);
  }

  static async searchObservationByJunctionId(
    medplum: MedplumClient,
    junctionId: string,
  ): Promise<Observation | undefined> {
    return await VitalObservation.searchOneResource(
      medplum,
      VitalObservation.toFhirQuery({ junctionId }),
    );
  }

  private static async upsertDailyObservationResource({
    medplum,
    observationData,
    existingResource,
    junctionId,
  }: {
    medplum: MedplumClient;
    observationData: Omit<Observation, "resourceType">;
    existingResource: Observation | undefined;
    junctionId: string;
  }): Promise<Observation> {
    if (existingResource?.id) {
      return await VitalObservation.patchResource(medplum, {
        id: existingResource.id,
        patch: observationData,
      });
    }

    // Conditional create settles concurrent writers that both missed the pre-create search.
    const created = await VitalObservation.createResourceIfNoneExist(medplum, {
      resource: observationData,
      ifNoneExistQuery: `identifier=${VitalObservation.Identifier.JunctionId}|${junctionId}`,
    });

    // If the conditional create returned the row another writer just created, patch our payload
    // over it so the last delivery wins instead of silently dropping this event's data.
    return created.id
      ? await VitalObservation.patchResource(medplum, { id: created.id, patch: observationData })
      : created;
  }

  static async getExistingJunctionIds(
    medplum: MedplumClient,
    junctionIds: string[],
  ): Promise<Set<string>> {
    if (junctionIds.length === 0) {
      return new Set();
    }

    const uniqueJunctionIds = Array.from(new Set(junctionIds));
    const existingIds = new Set<string>();
    const identifierSystem = VitalObservation.Identifier.JunctionId;
    const chunkSize = 50;

    for (let index = 0; index < uniqueJunctionIds.length; index += chunkSize) {
      const chunk = uniqueJunctionIds.slice(index, index + chunkSize);
      const observations = await VitalObservation.searchResources(medplum, {
        identifier: chunk.map((id) => `${identifierSystem}|${id}`).join(","),
        _elements: "identifier",
        _count: chunk.length.toString(),
      });

      for (const observation of observations) {
        const junctionIdentifier = observation.identifier?.find(
          (identifier) => identifier.system === identifierSystem,
        );
        if (junctionIdentifier?.value) {
          existingIds.add(junctionIdentifier.value);
        }
      }
    }

    return existingIds;
  }

  protected static async searchObservationsByPatient(
    medplum: MedplumClient,
    { patientId, loincCode, options }: SearchObservationsByPatientParams,
  ): Promise<Observation[]> {
    const filter = buildDateRangeFilter(options);

    return await VitalObservation.searchResources(medplum, {
      ...VitalObservation.toFhirQuery({ patientId, loincCode }),
      ...(filter ? { _filter: filter } : {}),
      _sort: options?.sort ?? "-date",
      _count: options?.limit ?? 100,
      _offset: options?.offset,
    });
  }

  protected static async searchObservationsByCustomCode(
    medplum: MedplumClient,
    { patientId, codeSystem, code, options }: SearchObservationsByCustomCodeParams,
  ): Promise<Observation[]> {
    const filter = buildDateRangeFilter(options);

    return await VitalObservation.searchResources(medplum, {
      subject: `Patient/${patientId}`,
      code: `${codeSystem}|${code}`,
      ...(filter ? { _filter: filter } : {}),
      _sort: options?.sort ?? "-date",
      _count: options?.limit ?? 100,
      _offset: options?.offset,
    });
  }

  static async exists(medplum: MedplumClient, junctionId: string): Promise<boolean> {
    const existing = await VitalObservation.searchObservationByJunctionId(medplum, junctionId);
    return existing !== undefined;
  }

  static async createCompletionMarker(
    medplum: MedplumClient,
    baseJunctionId: string,
    patientId: string,
  ): Promise<Observation> {
    const markerId = `${baseJunctionId}-complete`;
    const observationData = {
      identifier: [
        {
          system: VitalObservation.Identifier.JunctionId,
          value: markerId,
        },
      ],
      status: "final" as const,
      code: {
        coding: [
          {
            system: "http://vibrant.health",
            code: "wearable-processing-complete",
            display: "Wearable Data Processing Complete",
          },
        ],
        text: "Processing Complete Marker",
      },
      subject: {
        reference: `Patient/${patientId}`,
      },
    };

    return await VitalObservation.upsertResource(medplum, {
      data: observationData,
      query: {
        identifier: `${VitalObservation.Identifier.JunctionId}|${markerId}`,
      },
    });
  }

  static async hasCompletionMarker(
    medplum: MedplumClient,
    baseJunctionId: string,
  ): Promise<boolean> {
    const existing = await VitalObservation.searchObservationByJunctionId(
      medplum,
      `${baseJunctionId}-complete`,
    );
    return existing !== undefined;
  }

  private static getBackfillMarkerId({
    memberId,
    providerSlug,
    startDate,
    endDate,
  }: BackfillMarkerParams): string {
    return `backfill-${memberId}-${providerSlug}-${startDate}-${endDate}`;
  }

  static async createBackfillCompletionMarker(
    medplum: MedplumClient,
    { memberId, providerSlug, startDate, endDate, results }: CreateBackfillCompletionMarkerParams,
  ): Promise<Observation> {
    const markerId = VitalObservation.getBackfillMarkerId({
      memberId,
      providerSlug,
      startDate,
      endDate,
    });
    const noteText = results
      ? [
          `Activity: fetched=${results.activity?.fetched ?? 0}, created=${results.activity?.created ?? 0}`,
          `Sleep: fetched=${results.sleep?.fetched ?? 0}, created=${results.sleep?.created ?? 0}`,
          `Body: fetched=${results.body?.fetched ?? 0}, created=${results.body?.created ?? 0}`,
          `Workouts: fetched=${results.workouts?.fetched ?? 0}, created=${results.workouts?.created ?? 0}`,
        ].join(" | ")
      : undefined;

    const observationData = {
      identifier: [
        {
          system: VitalObservation.Identifier.JunctionId,
          value: markerId,
        },
      ],
      status: "final" as const,
      code: {
        coding: [
          {
            system: "http://vibrant.health",
            code: "wearable-backfill-complete",
            display: "Wearable Data Backfill Complete",
          },
        ],
        text: "Backfill Complete Marker",
      },
      subject: {
        reference: `Patient/${memberId}`,
      },
      effectivePeriod: {
        start: startDate,
        end: endDate,
      },
      note: noteText ? [{ text: noteText }] : undefined,
      component: [
        {
          code: {
            coding: [
              {
                system: "http://vibrant.health",
                code: "wearable-provider",
                display: "Wearable Provider",
              },
            ],
          },
          valueString: providerSlug,
        },
      ],
    };

    return await VitalObservation.upsertResource(medplum, {
      data: observationData,
      query: {
        identifier: `${VitalObservation.Identifier.JunctionId}|${markerId}`,
      },
    });
  }

  static async hasBackfillCompletionMarker(
    medplum: MedplumClient,
    marker: BackfillMarkerParams,
  ): Promise<boolean> {
    const markerId = VitalObservation.getBackfillMarkerId(marker);
    const existing = await VitalObservation.searchObservationByJunctionId(medplum, markerId);
    return existing !== undefined;
  }
}

function buildObservationIdentifiers({
  junctionId,
  providerSlug,
  extraIdentifiers,
}: {
  junctionId: string;
  providerSlug?: string;
  extraIdentifiers?: Identifier[];
}): Identifier[] {
  const identifiers: Identifier[] = [
    {
      system: VitalObservation.Identifier.JunctionId,
      value: junctionId,
    },
  ];

  if (providerSlug) {
    identifiers.push({
      system: VitalObservation.Identifier.ProviderSlug,
      value: providerSlug,
    });
  }

  if (extraIdentifiers?.length) {
    identifiers.push(...extraIdentifiers);
  }

  return identifiers;
}

function buildObservationComponents({
  statistics,
  components,
  unit,
  unitCode,
}: {
  statistics?: DailyVitalObservationData["statistics"];
  components?: DailyVitalObservationData["components"];
  unit: string;
  unitCode: string;
}): ObservationComponent[] | undefined {
  const builtComponents: ObservationComponent[] = [];

  if (statistics) {
    if (statistics.average !== undefined) {
      builtComponents.push({
        code: ObservationStatistics.Average,
        valueQuantity: {
          value: statistics.average,
          unit,
          system: "http://unitsofmeasure.org",
          code: unitCode,
        },
      });
    }

    if (statistics.min !== undefined) {
      builtComponents.push({
        code: ObservationStatistics.Minimum,
        valueQuantity: {
          value: statistics.min,
          unit,
          system: "http://unitsofmeasure.org",
          code: unitCode,
        },
      });
    }

    if (statistics.max !== undefined) {
      builtComponents.push({
        code: ObservationStatistics.Maximum,
        valueQuantity: {
          value: statistics.max,
          unit,
          system: "http://unitsofmeasure.org",
          code: unitCode,
        },
      });
    }

    if (statistics.count !== undefined) {
      builtComponents.push({
        code: ObservationStatistics.Count,
        valueQuantity: {
          value: statistics.count,
          unit: "{observations}",
          system: "http://unitsofmeasure.org",
          code: "{observations}",
        },
      });
    }
  }

  if (components?.length) {
    for (const component of components) {
      const nextComponent: ObservationComponent = {
        code: component.code,
      };

      if (component.value !== undefined) {
        nextComponent.valueQuantity = {
          value: component.value,
          unit: component.unit || unit,
          system: "http://unitsofmeasure.org",
          code: component.unitCode || unitCode,
        };
      } else if (component.valueString !== undefined) {
        nextComponent.valueString = component.valueString;
      } else if (component.valuePeriod !== undefined) {
        nextComponent.valuePeriod = component.valuePeriod;
      }

      builtComponents.push(nextComponent);
    }
  }

  return builtComponents.length > 0 ? builtComponents : undefined;
}

function buildSampledData({
  sampledData,
  unit,
  unitCode,
}: {
  sampledData?: DailyVitalObservationData["sampledData"];
  unit: string;
  unitCode: string;
}): SampledData | undefined {
  if (!sampledData) {
    return undefined;
  }

  return {
    origin: {
      value: sampledData.origin,
      unit,
      system: "http://unitsofmeasure.org",
      code: unitCode,
    },
    period: sampledData.periodMs,
    dimensions: 1,
    data: sampledData.data,
  };
}

/**
 * Extensions are patched by url, so an entry with a `null` value removes just that extension and
 * leaves the rest of the resource's extensions alone. `removeWhenMissing` is only meaningful when
 * patching an existing row: a value that stops being derived must lose its derivation stamp.
 */
function buildDailyObservationExtensions({
  timezoneOffsetSeconds,
  valueDerivation,
  removeWhenMissing,
}: {
  timezoneOffsetSeconds?: number | null;
  valueDerivation?: WearableValueDerivation;
  removeWhenMissing?: boolean;
}): Observation["extension"] {
  const extensions: Observation["extension"] = [];
  const normalizedOffsetSeconds = normalizeTimezoneOffsetSeconds(timezoneOffsetSeconds);

  if (normalizedOffsetSeconds !== undefined) {
    extensions.push({
      url: VitalObservation.Extension.TimezoneOffsetSeconds,
      valueInteger: normalizedOffsetSeconds,
    });
  } else if (removeWhenMissing) {
    extensions.push({
      url: VitalObservation.Extension.TimezoneOffsetSeconds,
      valueInteger: null,
    } as unknown as NonNullable<Observation["extension"]>[number]);
  }

  if (valueDerivation) {
    extensions.push({
      url: VitalObservation.Extension.ValueDerivation,
      valueCode: valueDerivation,
    });
  } else if (removeWhenMissing) {
    extensions.push({
      url: VitalObservation.Extension.ValueDerivation,
      valueCode: null,
    } as unknown as NonNullable<Observation["extension"]>[number]);
  }

  return extensions.length > 0 ? extensions : undefined;
}

function buildDayPeriod(date: string, timezoneOffsetSeconds?: number | null): Period {
  const localDay = parseLocalDay(date);
  const normalizedOffsetSeconds = normalizeTimezoneOffsetSeconds(timezoneOffsetSeconds);
  if (localDay && normalizedOffsetSeconds !== undefined) {
    const startMs =
      Date.UTC(localDay.year, localDay.monthIndex, localDay.day, 0, 0, 0) -
      normalizedOffsetSeconds * 1000;
    const endMs =
      Date.UTC(localDay.year, localDay.monthIndex, localDay.day + 1, 0, 0, 0) -
      normalizedOffsetSeconds * 1000 -
      1000;

    return {
      start: formatIsoSecond(startMs),
      end: formatIsoSecond(endMs),
    };
  }

  return {
    start: `${date}T00:00:00Z`,
    end: `${date}T23:59:59Z`,
  };
}

function parseLocalDay(
  date: string,
): { year: number; monthIndex: number; day: number } | undefined {
  const match = LOCAL_DAY_REGEX.exec(date);
  if (!match) {
    return undefined;
  }

  return {
    year: Number(match[1]),
    monthIndex: Number(match[2]) - 1,
    day: Number(match[3]),
  };
}

function formatIsoSecond(ms: number): string {
  return new Date(ms).toISOString().replace(".000Z", "Z");
}

function getLocalDateFromInstant({
  instant,
  timezoneOffsetSeconds,
}: {
  instant: string;
  timezoneOffsetSeconds?: number;
}): string | undefined {
  const normalizedOffsetSeconds = normalizeTimezoneOffsetSeconds(timezoneOffsetSeconds);
  if (normalizedOffsetSeconds === undefined) {
    return undefined;
  }

  const instantMs = Date.parse(instant);
  if (!Number.isFinite(instantMs)) {
    return undefined;
  }

  return formatIsoSecond(instantMs + normalizedOffsetSeconds * 1000).slice(0, 10);
}

function buildDateRangeFilter(options?: { dateGe?: string; dateLe?: string }): string | undefined {
  const filterParts: string[] = [];

  if (options?.dateGe) {
    filterParts.push(`date ge ${options.dateGe}`);
  }

  if (options?.dateLe) {
    filterParts.push(`date le ${options.dateLe}`);
  }

  return filterParts.length > 0 ? filterParts.join(" and ") : undefined;
}

function getLoincCoding(observation: Observation): Coding | undefined {
  return observation.code?.coding?.find((coding) => coding.system === "http://loinc.org");
}

function getPrimaryCoding(observation: Observation): Coding | undefined {
  const loincCoding = getLoincCoding(observation);
  if (loincCoding) {
    return loincCoding;
  }

  return observation.code?.coding?.find((coding) => !coding.system);
}
